import { List, Map, Set } from "immutable";
import { isNil, map } from "lodash";
// import { Stopwatch } from "performance-stopwatch";
import createCachedSelector from "re-reselect";
import { createSelector } from "reselect";

import {
  TIMELINE_AGE_LABEL_FONT_SIZE,
  TIMELINE_CONTEXT_MAJOR_TICK_SPACING,
  TIMELINE_HEADER_AGE_TICK_SPACING,
  TIMELINE_HEADER_MAJOR_TICK_SPACING
} from "../../constants";
import CheckboxStates from "../../constants/checkboxStates";
import { DateFormatIdentifiers } from "../../constants/dateFormats";
import { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import timelineViewConstants from "../../constants/styles/timelineView";
import timelineGroupOptions, {
  TIMELINE_GROUP_SHOW_ALL,
  UNALLOCATED_GROUP_PREFIX
} from "../../constants/timelineGroupOptions";
import timelineLayoutOptions from "../../constants/timelineLayoutOptions";
import { appStateSelectors } from "../../modules/appState";
import { calendarMarkerSelectors } from "../../modules/calendarMarkers";
import { definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import dateFunctions, { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import dateByAddingSpan from "../../modules/definition/calendar/dateFunctions/dateByAddingSpan";
import dateSubtractionFunctions from "../../modules/definition/calendar/dateFunctions/dateSubtractionFunctions";
import { stringFromDate } from "../../modules/definition/calendar/dateFunctions/stringFromDate";
import {
  roundedTimespanForScale,
  stringFromTimespan,
  timespanForComponents,
  timespanForPrecisionAndUnits,
  timespanToApproximateSeconds
} from "../../modules/definition/calendar/dateFunctions/timespanFunctions";
import {
  getDaysInMonth,
  getHoursInDayFromCalendar,
  getMonthsInYearFromCalendar,
  getWeekdaysFromCalendar
} from "../../modules/definition/calendar/selectors";
import displayCacheSelectors from "../../modules/display/selectors";
import { itemSelectors } from "../../modules/items";
import sortFunctions from "../../modules/items/sortFunctions";
import { relationshipSelectors } from "../../modules/relationships";
import { superAndChildrenSelectors } from "../../modules/superAndChildrenById";
import { timelineRangeSelectors } from "../../modules/timelineRange";
import { uiStateSelectors } from "../../modules/uiState";
import { immutableRecords, Precision } from "../../types";
import { calcTextSize } from "../../utils/calcTextSize";
// import debugUtils from "../../utils/debugUtils";
import floatToInt from "../../utils/floatToInt";
import { doSetsIntersect, listToMap } from "../../utils/immutableExtensions";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import rectUtils from "../../utils/rectUtils";
import workViewSelectors from "../workView/selectors";
import layoutFunctions from "./layoutFunctions";
import timelineUtils from "./utils";

// Sort Events - Makes sure selected events are in correct order...
const getDatedItemsForView = createCachedSelector(
  // fetch parameters for last calculate function
  workViewSelectors.getItemsInGroupForView,
  itemSelectors.getItemsById,
  // Calculate result
  (items, itemsById) => {
    return items
      .filter(e => isDateValid(e.startDate))
      .map(e =>
        e.update("children", children => children.filter(childId => isDateValid(itemsById.get(childId).startDate)))
      );
  }
)(workViewSelectors.getViewId);

const getEventsOutOfRangeNumber = createSelector(
  getDatedItemsForView,
  timelineRangeSelectors.getRangeFrom,
  timelineRangeSelectors.getRangeTo,
  (items, dateRangeFrom, dateRangeTo) => {
    let left = 0;
    let right = 0;
    items.forEach(e => {
      if (isDateValid(dateRangeFrom) && e.startDate.timestamp < dateRangeFrom.timestamp) {
        left++;
      } else if (isDateValid(dateRangeTo) && e.startDate.timestamp > dateRangeTo.timestamp) {
        right++;
      }
    });
    return { left, right };
  }
);

// Considering the events are out out set range
const getActualDateFrom = createSelector(
  itemSelectors.getDateRangeForTimelineContent,
  timelineRangeSelectors.getRangeFrom,
  (contentRange, dateFrom) => {
    return dateFrom &&
      isDateValid(dateFrom) &&
      isDateValid(contentRange.from) &&
      contentRange.from.timestamp < dateFrom.timestamp
      ? contentRange.from
      : dateFrom;
  }
);

const getActualDateTo = createSelector(
  itemSelectors.getDateRangeForTimelineContent,
  timelineRangeSelectors.getRangeTo,
  (contentRange, dateTo) => {
    const result =
      dateTo && isDateValid(dateTo) && isDateValid(contentRange.to) && contentRange.to.timestamp > dateTo.timestamp
        ? contentRange.to
        : dateTo;
    return isDateValid(result) ? result : null;
  }
);

// Sort Events - Makes sure selected events are in correct order...
const getSortedEvents = createCachedSelector(
  // get the items with dates and sort them into date order
  getDatedItemsForView,
  // Calculate result
  items => items.sort(sortFunctions.byStart).map((e, index) => e.set("index", index))
)(workViewSelectors.getViewId);

/**
 * NOTE: viewId must always be 2nd, because that is where workViewSelectors expects it to be!
 * @param {TimelineDisplayRecord} data
 * @param {string} viewId
 * @param {Set} ongoingDescendantsSet
 * @param {List} topLevelItems
 * @param {*} expandedItems
 * @param {number} unitScale
 * @param {number} fontSize
 * @param {CalendarRecord} calendar
 * @param {boolean} showItemCard
 * @param {*} eventLayout
 */
// Layout Events - Works out the x & y position for every event in the view
const layoutEvents = createCachedSelector(
  // fetch parameters for last calculate function
  data => data,
  (data, viewId) => viewId,
  (_0, _1, ongoingDescendantsSet) => ongoingDescendantsSet,
  (_0, _1, _2, topLevelItems) => topLevelItems,
  (_0, _1, _2, _3, expandedItems) => expandedItems,
  (data, viewId, _2, _3, _4, unitScale) => unitScale,
  (_0, _1, _2, _3, _4, _5, fontSize) => fontSize,
  (_0, _1, _2, _3, _4, _5, _6, calendar) => dateFunctions.dateForTodayUnlessOverride(calendar),
  (_0, _1, _2, _3, _4, _5, _6, _7, showItemCard) => showItemCard,
  (_0, _1, _2, _3, _4, _5, _6, _7, _8, eventLayout) => eventLayout,
  // Calculate result
  (
    data,
    viewId,
    ongoingDescendantsSet,
    topLevelItems,
    expandedItems,
    unitScale,
    fontSize,
    dateForNow,
    showItemCard,
    eventLayout
  ) => {
    // const sw = new Stopwatch();
    // sw.start("layoutEvents start");

    if (!unitScale) {
      // eslint-disable-next-line no-console
      console.log(`layoutEvents() called without unitScale passed in... that is bad!`);
    }

    const layoutFn =
      eventLayout === timelineLayoutOptions.CLEAR_ALL
        ? layoutFunctions.calcNodeLayoutClearAll
        : eventLayout === timelineLayoutOptions.WATERFALL
          ? layoutFunctions.calcNodeLayoutWaterfall
          : layoutFunctions.calcNodeLayoutCompact;
    const result = layoutFn(
      viewId,
      data,
      ongoingDescendantsSet,
      topLevelItems,
      expandedItems,
      unitScale,
      fontSize,
      dateForNow,
      showItemCard,
      eventLayout
    );

    // sw.total("layoutEvents end");

    return result;
  }
)(workViewSelectors.getViewId);

/**
 * Checks whether this item should be displayed for this group ID.
 * Looks for relationship between:
 *  [itemId or child of itemId] to [groupId or child of groupId]
 * Or:
 *  [itemId] is [groupId or child of groupId]
 * @param {string} itemId
 * @param {Set} targetIdSet
 * @param {Set} relationshipsForGroup
 * @param {Set} relationshipsForItem
 */
const itemBelongsInGroup = createCachedSelector(
  // input
  (itemId, _targetIdSet, _relationshipsForGroup, _relationshipsForItem) => itemId,
  (itemId, targetIdSet, _relationshipsForGroup, _relationshipsForItem) => targetIdSet,
  (itemId, targetIdSet, relationshipsForGroup, _relationshipsForItem) => relationshipsForGroup,
  (itemId, targetIdSet, relationshipsForGroup, relationshipsForItem) => relationshipsForItem,
  // output
  (itemId, targetIdSet, relationshipsForGroup, relationshipsForItem) => {
    return targetIdSet.contains(itemId) || doSetsIntersect(relationshipsForItem, relationshipsForGroup);
  }
)((itemId, groupId, _relationshipsById, _descendantsById) => `${itemId}-${groupId}`);

/**
 * Checks whether this item should be displayed for this group ID.
 * Looks for relationship between:
 *  [itemId or child of itemId] to [groupId or child of groupId]
 * Or:
 *  [itemId] is [groupId or child of groupId]
 * @param {string} itemId
 * @param {Set} excludeRelationshipIds
 * @param {Set} relationshipsForItem
 */
const itemBelongsInUnallocatedGroup = createCachedSelector(
  // input
  (itemId, _excludeRelationshipIds, _relationshipsForItem) => itemId,
  (itemId, excludeRelationshipIds, _relationshipsForItem) => excludeRelationshipIds,
  (itemId, _excludeRelationshipIds, relationshipsForItem) => relationshipsForItem,
  // output
  (itemId, excludeRelationshipIds, relationshipsForItem) => {
    return !doSetsIntersect(relationshipsForItem, excludeRelationshipIds);
  }
)((itemId, typeId) => `${itemId}-${typeId}`);

const _getRelationshipIdsForItem = item => item.references.concat(item.inverseReferences);
const _getRelationshipIdsByItem = itemsById => itemsById.map(item => _getRelationshipIdsForItem(item));

const _getRelationshipIdsByItemAsString = createSelector(
  // inputs
  itemsById => itemsById,
  // output
  itemsById => JSON.stringify(_getRelationshipIdsByItem(itemsById).toJS())
);

const getRelationshipIdsByItem = createSelector(
  // inputs
  _getRelationshipIdsByItemAsString,
  // output
  str => Map(JSON.parse(str)).map(arr => Set(arr))
);

const _getIdsFromList = items => items.map(i => i.id);

/**
 * @param {List} items
 * @param {string} key
 */
const _getIdsFromListAsString = createCachedSelector(
  // inputs
  items => items,

  // output
  items => JSON.stringify(_getIdsFromList(items).toArray())
)((items, key) => key);

/**
 * @param {List} items
 * @param {string} key
 */
const getCachedIdsFromList = createCachedSelector(
  // inputs
  _getIdsFromListAsString,
  (items, key) => key,
  // output
  str => List(JSON.parse(str))
)((items, key) => key);

const getRelationshipsForItemAndDescendants = createCachedSelector(
  // inputs
  (itemId, _descendantsById, _relationshipIdsByItem) => itemId,
  (itemId, descendantsById, _relationshipIdsByItem) => descendantsById,
  (itemId, descendantsById, relationshipIdsByItem) => relationshipIdsByItem,
  // output
  (itemId, descendantsById, relationshipIdsByItem) => {
    const itemIds = descendantsById.get(itemId).add(itemId);
    const relationshipIds = Set().withMutations(relIdSet =>
      itemIds.forEach(itemId => {
        relIdSet.union(relationshipIdsByItem.get(itemId));
      })
    );
    return { itemIds, relationshipIds };
  }
)(itemId => itemId);

const getRelationshipsForItemsOfTypeAndDescendants = createCachedSelector(
  // inputs
  (itemIds, _descendantsById, _relationshipIdsByItem, _typeId) => itemIds,
  (itemIds, descendantsById, _relationshipIdsByItem, _typeId) => descendantsById,
  (itemIds, descendantsById, relationshipIdsByItem, _typeId) => relationshipIdsByItem,
  // output
  (itemIds, descendantsById, relationshipIdsByItem) => {
    const result = Set().withMutations(relIdSet =>
      itemIds.forEach(itemId => {
        const { relationshipIds } = getRelationshipsForItemAndDescendants(
          itemId,
          descendantsById,
          relationshipIdsByItem
        );
        relIdSet.union(relationshipIds);
      })
    );
    return result;
  }
)((itemIds, descendantsById, relationshipIdsByItem, typeId) => typeId);

/**
 * Takes a list of sorted events and returns those that belong in a particular group
 * @param {List} sortedEventIds
 * @param {Map} descendantsById
 * @param {Set} targetIdSet
 * @param {Set} relationshipsForGroup
 * @param {Map} relationshipIdsByItem
 */
const getEventsForGroup = (
  sortedEventIds,
  descendantsById,
  targetIdSet,
  relationshipsForGroup,
  relationshipIdsByItem
) => {
  return sortedEventIds.filter(id => {
    const { relationshipIds } = getRelationshipsForItemAndDescendants(id, descendantsById, relationshipIdsByItem);
    return itemBelongsInGroup(id, targetIdSet, relationshipsForGroup, relationshipIds);
  });
};

/**
 * Takes a list of sorted events and returns those that belong in a particular group
 * @param {List} sortedEventIds
 * @param {Map} descendantsById
 * @param {Set} excludeRelationshipIds
 * @param {Map} relationshipIdsByItem
 */
const getEventsForUnallocatedGroup = (
  sortedEventIds,
  descendantsById,
  excludeRelationshipIds,
  relationshipIdsByItem
) => {
  return sortedEventIds.filter(id => {
    const { relationshipIds } = getRelationshipsForItemAndDescendants(id, descendantsById, relationshipIdsByItem);
    return itemBelongsInUnallocatedGroup(id, excludeRelationshipIds, relationshipIds);
  });
};

/**
 *
 * @param {string} groupId            [0]
 * @param {string} itemLabel          [1]
 * @param {string} typeLabel          [2]
 * @param {string} typeIcon           [3]
 * @param {string} typeColor          [4]
 * @param {List} sortedEvents         [5]
 * @param {Map} relationshipsById     [6]
 * @param {Map} relationshipIdsByItem [7]
 * @param {Map} descendantsById       [8]
 * @param {Map} itemIdsByType         [9]
 * @param {string} viewId             [10]
 */
const getTimelineGroup = createCachedSelector(
  // input
  (...params) => params[0],
  (...params) => params[1],
  (...params) => params[2],
  (...params) => params[3],
  (...params) => params[4],
  (...params) => params[5],
  (...params) => params[6],
  (...params) => params[7],
  (...params) => params[8],
  (...params) => params[9],
  // output
  (
    groupId,
    itemLabel,
    typeLabel,
    typeIcon,
    typeColor,
    sortedEventIds,
    relationshipsById,
    relationshipIdsByItem,
    descendantsById,
    itemIdsByType
  ) => {
    // Special Case 1: Everything in  single group
    if (groupId === TIMELINE_GROUP_SHOW_ALL) {
      return immutableRecords.TimelineViewGroupRecord({
        id: groupId,
        showHeader: false,
        eventIds: sortedEventIds
      });
    }
    // Special case 2: Unallocated group
    if (groupId.startsWith(UNALLOCATED_GROUP_PREFIX)) {
      const typeId = groupId.substr(UNALLOCATED_GROUP_PREFIX.length);
      const itemIdsForType = itemIdsByType.get(typeId);
      const excludeRelationshipIds = getRelationshipsForItemsOfTypeAndDescendants(
        itemIdsForType,
        descendantsById,
        relationshipIdsByItem,
        typeId
      );
      return immutableRecords.TimelineViewGroupRecord({
        id: groupId,
        label: `No ${typeLabel} (Unallocated)`,
        icon: typeIcon,
        iconColor: typeColor,
        eventIds: getEventsForUnallocatedGroup(
          sortedEventIds,
          descendantsById,
          excludeRelationshipIds,
          relationshipIdsByItem
        )
      });
    }

    // Normal case: a group associated with a selected event

    const { itemIds: targetIdSet, relationshipIds: relationshipsForGroup } = getRelationshipsForItemAndDescendants(
      groupId,
      descendantsById,
      relationshipIdsByItem
    );

    return immutableRecords.TimelineViewGroupRecord({
      id: groupId,
      label: getLabelWithUntitled(itemLabel),
      icon: typeIcon,
      iconColor: typeColor,
      eventIds: getEventsForGroup(
        sortedEventIds,
        descendantsById,
        targetIdSet,
        relationshipsForGroup,
        relationshipIdsByItem
      )
    });
  }
)((...params) => `${params[0]}-${params[10]}`);

// Merges the event layout with the current selection.
// Receives: state, viewId
const getTimelineViewData = createCachedSelector(
  // fetch parameters for last calculate function
  getSortedEvents,
  appStateSelectors.getViewTimelineGroupIds,
  displayCacheSelectors.getDisplayItemsById,
  definitionSelectors.getTypesById,
  relationshipSelectors.getRelationshipsById,
  superAndChildrenSelectors.getDescendantsMapFromState,
  itemSelectors.getItemIdsByType,
  itemSelectors.getItemIndexesInTreeOrderForAllTypes,
  workViewSelectors.getViewId,
  // Calculate result
  (
    sortedEvents,
    inGroupIds,
    displayItemMap,
    typesById,
    relationshipsById,
    descendantsById,
    itemIdsByType,
    globalItemIndexes,
    viewId
  ) => {
    // const sw = new Stopwatch();
    // sw.start("getTimelineViewData start");

    const eventsById = listToMap(sortedEvents);
    const sortedEventIds = getCachedIdsFromList(sortedEvents, viewId);
    const groupIds = inGroupIds.size
      ? inGroupIds.sort((a, b) => globalItemIndexes.get(a) - globalItemIndexes.get(b))
      : List([TIMELINE_GROUP_SHOW_ALL]);
    const relationshipIdsByItem = getRelationshipIdsByItem(displayItemMap);
    const groups = groupIds.map(groupId => {
      const item = displayItemMap.get(groupId);
      const typeId = item ? item.type : groupId.substr(UNALLOCATED_GROUP_PREFIX.length);
      const type = typesById.get(typeId);

      const result = getTimelineGroup(
        groupId,
        item ? item.label : null,
        type ? type.label : null,
        type ? type.icon : null,
        type ? type.color : null,
        sortedEventIds,
        relationshipsById,
        relationshipIdsByItem,
        descendantsById,
        itemIdsByType
      );
      return result;
    });

    const allEventIds = groups.reduce((result, g) => result.union(g.eventIds), Set());

    const result = immutableRecords.TimelineDisplayRecord({
      groups,
      eventsById: eventsById.filter(e => allEventIds.contains(e.id)),
      sortedEventIds: sortedEventIds.filter(id => allEventIds.contains(id))
    });

    // sw.total("getTimelineViewData end");

    return result;
  }
)(workViewSelectors.getViewId);

const getTimelineViewSelectedData = createCachedSelector(
  getTimelineViewData,
  uiStateSelectors.getSelectedItems,
  (data, selectedIds) => {
    return data
      .update("groups", groups =>
        groups.map(g => g.update("eventIds", eventIds => eventIds.filter(id => selectedIds.contains(id))))
      )
      .update("eventsById", eventsById => eventsById.filter(e => selectedIds.contains(e.id)))
      .update("sortedEventIds", ids => ids.filter(id => selectedIds.contains(id)));
  }
)(workViewSelectors.getViewId);

const getTimelineViewSortedItemIds = createCachedSelector(
  // input
  getTimelineViewData,
  // output
  data => data.sortedEventIds
)(workViewSelectors.getViewId);

// Merges the event layout with the current selection.
// Receives: state, viewId
const getTimelineViewDataForItem = createSelector(
  // fetch parameters for last calculate function
  workViewSelectors.getTimelineViewDisplayItemForId,
  itemSelectors.getItemsById,
  (_state, _itemId, unitScale, _fontSize, _showItemCard) => unitScale,
  (state, _itemId, _unitScale, _fontSize, _showItemCard) =>
    dateFunctions.dateForNow(calendarSelectors.getCalendar(state)),
  (_state, _itemId, _unitScale, fontSize, _showItemCard) => fontSize,
  (_state, _itemId, _unitScale, fontSize, showItemCard) => showItemCard,
  (_state, _itemId, _unitScale, fontSize, _showItemCard, viewId) => viewId,
  (state, itemId) => itemSelectors.getSetOfItemIdsWithOngoingDescendants(state).has(itemId),
  // Calculate result
  (obj, itemsById, unitScale, dateForNow, fontSize, showItemCard, viewId, hasOngoingDescendants) => {
    const position = layoutFunctions.calcEventInternalLayout(
      viewId,
      obj,
      unitScale,
      fontSize,
      dateForNow,
      hasOngoingDescendants,
      showItemCard
    );
    return { itemDisplay: obj, position };
  }
);

const getScrollRange = (scrollLeft, scrollRight, dateRangeFrom, dateRangeTo, unitScale) => {
  const fromScroll = isDateValid(dateRangeFrom) ? dateRangeFrom.timestamp / unitScale : null;
  const toScroll = isDateValid(dateRangeTo) ? dateRangeTo.timestamp / unitScale : null;

  return {
    left: !isNil(fromScroll) ? Math.max(scrollLeft, fromScroll) : scrollLeft,
    right: !isNil(toScroll) ? Math.min(scrollRight, toScroll) : scrollRight
  };
};

// Timeline Header Tick Calculations

const getCalendar = (calendar, _viewId, _unitScale, _leftPixel, _rightPixel, _fontSize) => calendar;
const getUnitScale = (_calendar, _viewId, unitScale, _leftPixel, _rightPixel, _fontSize) => unitScale;
const getLeftPixel = (_calendar, _viewId, _unitScale, leftPixel, _rightPixel, _fontSize) => leftPixel;
const getRightPixel = (_calendar, _viewId, _unitScale, _leftPixel, rightPixel, _fontSize) => rightPixel;
const getFontSize = (_calendar, _viewId, _unitScale, _leftPixel, _rightPixel, fontSize) => fontSize;

/**
 *
 * @param {calendar} calendar         [0]
 * @param {string} viewId             [1]
 * @param {number} unitScale          [2]
 * @param {number} leftPixel          [3]
 * @param {number} rightPixel         [4]
 * @param {number} fontSize           [5]
 * @param {DateRecord} dateRangeFrom  [6]
 * @param {DateRecord} dateRangeTo    [7]
 */
const getLabelScrollRange = createCachedSelector(
  // input
  (...params) => params[3], // leftPixel
  (...params) => params[4], // rightPixel
  (...params) => params[6], // dateRangeFrom
  (...params) => params[7], // dateRangeTo
  (...params) => params[2], // unitScale
  // output
  getScrollRange
)((calendar, viewId) => viewId);

const getTickInterval = createCachedSelector(
  // fetch parameters for last calculate function
  getCalendar,
  getUnitScale,
  getFontSize,
  // Calculate result
  (calendar, unitScale, fontSize) => {
    const spacing = scalePixelsToFontSize(TIMELINE_HEADER_MAJOR_TICK_SPACING, fontSize);
    const result = dateFunctions.dateTickIntervals(calendar, unitScale, spacing);
    return result;
  }
)(workViewSelectors.getViewId);

// Not exported
const getContextTickInterval = createCachedSelector(
  // fetch parameters for last calculate function
  getCalendar,
  getUnitScale,
  getFontSize,
  // Calculate result
  (calendar, unitScale, fontSize) => {
    const spacing = scalePixelsToFontSize(TIMELINE_CONTEXT_MAJOR_TICK_SPACING, fontSize);
    const result = dateFunctions.dateTickIntervals(calendar, unitScale, spacing);
    return result;
  }
)(workViewSelectors.getViewId);

// Not exported
const getAgeTickInterval = createCachedSelector(
  // fetch parameters for last calculate function
  getCalendar,
  getUnitScale,
  getFontSize,
  // Calculate result
  (calendar, unitScale, fontSize) => {
    const spacing = scalePixelsToFontSize(TIMELINE_HEADER_AGE_TICK_SPACING, fontSize);
    const result = dateFunctions.dateTickIntervals(calendar, unitScale, spacing);
    return result;
  }
)(workViewSelectors.getViewId);

// Add an offset of major span to the edge date
const getEdgeTickInner = (calendar, unitScale, edgePixel, tickInterval, isRight) => {
  const edgeTs = floatToInt(edgePixel * unitScale);
  const edgeDate = dateFunctions.dateWithTimestamp(calendar, edgeTs, Precision.Second);
  const edgeTickDate = dateFunctions.dateAtEvenSpanBeforeDate(calendar, edgeDate, tickInterval.majorSpan);
  const finalDate =
    isRight && edgeTickDate.timestamp !== edgeDate.timestamp
      ? dateByAddingSpan(calendar, edgeTickDate, tickInterval.majorSpan)
      : edgeTickDate;
  return finalDate;
};

const getFirstTickInner = (calendar, unitScale, leftPixel, tickInterval) => {
  return getEdgeTickInner(calendar, unitScale, leftPixel, tickInterval);
};

const getLastTickInner = (calendar, unitScale, rightPixel, tickInterval) => {
  return getEdgeTickInner(calendar, unitScale, rightPixel, tickInterval, true);
};

// Not exported
const getFirstTick = createCachedSelector(
  // fetch parameters for last calculate function
  getCalendar,
  getUnitScale,
  getLeftPixel,
  getTickInterval,
  // Calculate result
  getFirstTickInner
)(workViewSelectors.getViewId);

// Not exported - used internally
const calcHeaderLabels = (calendar, startDate, unitScale, rightTs, tickSpan, tickFormat) => {
  const formatOptions = {
    dateFormat: DateFormatIdentifiers.Custom,
    customFormat: tickFormat
  };
  let ticks = [];

  // Major Ticks
  let markDate = startDate,
    nextDate = dateFunctions.dateByAddingSpan(calendar, markDate, tickSpan);

  // TODO: This test is only required for absolute date format...
  if (nextDate.era !== markDate.era) {
    nextDate = dateFunctions.dateAtEvenSpanBeforeDate(calendar, nextDate, tickSpan);
  }

  while (markDate.timestamp <= rightTs) {
    const text = stringFromDate(calendar, markDate, formatOptions);
    ticks.push({
      pixel: floatToInt(markDate.timestamp / unitScale),
      label: text
    });
    markDate = nextDate;
    nextDate = dateFunctions.dateByAddingSpan(calendar, nextDate, tickSpan);

    // TODO: This test is only required for absolute date format...
    if (nextDate.era !== markDate.era) {
      const testDate = dateFunctions.dateAtEvenSpanBeforeDate(calendar, nextDate, tickSpan);
      if (testDate.timestamp !== markDate.timestamp) {
        nextDate = testDate;
      }
    }
  }

  return ticks;
};

const calcAgeLabels = (calendar, startDate, unitScale, rightTs, tickSpan, itemStartDate, itemEndDate, widthMap) => {
  let ticks = [];

  if (itemStartDate && itemStartDate.timestamp) {
    let markDate = startDate.timestamp < itemStartDate.timestamp ? itemStartDate : startDate;
    let previousDate = markDate;
    let ageSpan = dateSubtractionFunctions.timespanBetweenDates(calendar, itemStartDate, markDate);

    const monthsInYear = getMonthsInYearFromCalendar(calendar);
    const daysInMonth = getDaysInMonth(calendar, markDate.era, markDate.year, markDate.month);
    const daysInWeek = getWeekdaysFromCalendar(calendar).size;
    const hoursInDay = getHoursInDayFromCalendar(calendar);
    let unitChange = false;

    while (previousDate.timestamp <= rightTs) {
      let nextTickSpan = tickSpan;
      let objectAge = null;
      let isStart = false;
      let isEnd = false;
      const pixel = floatToInt(markDate.timestamp / unitScale);

      // Calculate object age text
      if (markDate.timestamp == itemStartDate.timestamp) {
        objectAge = "Start";
        isStart = true;
      } else if (itemEndDate && markDate.timestamp >= itemEndDate.timestamp) {
        objectAge = "End";
        isEnd = true;
      } else {
        objectAge = stringFromTimespan(ageSpan, 1);
      }

      // We always want show ticks for end date or change in unit type. Remove the previous tick if needed to make space.
      if ((isEnd || unitChange) && ticks.length && !ticks[ticks.length - 1].isStart) {
        const textSize = calcTextSize(ticks[ticks.length - 1].label, TIMELINE_AGE_LABEL_FONT_SIZE, widthMap, 250);
        if (pixel < ticks[ticks.length - 1].pixel + textSize.size.width + 10) {
          ticks.splice(-1, 1);
        }
      }

      // Add the tick
      ticks.push({
        pixel: pixel,
        label: objectAge,
        isStart: isStart,
        isEnd: isEnd
      });

      // Calculate next tick
      const min = 2;
      const units = 1;
      unitChange = false;
      if (ageSpan.years) {
        if (ageSpan.years < min && !tickSpan.years) {
          nextTickSpan = timespanForComponents(0, ageSpan.years * monthsInYear + units, 0, 0, 0, 0, 0);
        } else {
          nextTickSpan = timespanForComponents(
            tickSpan.years ? ageSpan.years + tickSpan.years : ageSpan.years + units,
            0,
            0,
            0,
            0,
            0,
            0
          );
        }
      } else if (ageSpan.months) {
        if (ageSpan.months < min && !tickSpan.months) {
          nextTickSpan = timespanForComponents(0, 0, daysInWeek / (ageSpan.months * daysInMonth) + units, 0, 0, 0, 0);
        } else if (ageSpan.months >= monthsInYear * min - 1) {
          nextTickSpan = timespanForComponents(min, 0, 0, 0, 0, 0, 0);
          unitChange = true;
        } else {
          nextTickSpan = timespanForComponents(
            ageSpan.years,
            tickSpan.months ? ageSpan.months + tickSpan.months : ageSpan.months + units,
            0,
            0,
            0,
            0,
            0
          );
        }
      } else if (ageSpan.weeks) {
        if (ageSpan.weeks < min && !tickSpan.weeks) {
          nextTickSpan = timespanForComponents(0, 0, 0, daysInWeek * ageSpan.weeks + units, 0, 0, 0);
        } else if (ageSpan.weeks >= (daysInMonth / daysInWeek) * min - 1) {
          nextTickSpan = timespanForComponents(0, min, 0, 0, 0, 0, 0);
          unitChange = true;
        } else {
          nextTickSpan = timespanForComponents(
            ageSpan.years,
            ageSpan.months,
            tickSpan.weeks ? ageSpan.weeks + tickSpan.weeks : ageSpan.weeks + units,
            0,
            0,
            0,
            0
          );
        }
      } else if (ageSpan.days) {
        if (ageSpan.days < min && !tickSpan.days) {
          nextTickSpan = timespanForComponents(0, 0, 0, 0, hoursInDay * ageSpan.days + units, 0, 0);
        } else if (ageSpan.days >= daysInWeek * min - 1) {
          nextTickSpan = timespanForComponents(0, 0, min, 0, 0, 0, 0);
          unitChange = true;
        } else {
          nextTickSpan = timespanForComponents(
            ageSpan.years,
            ageSpan.months,
            ageSpan.weeks,
            tickSpan.days ? ageSpan.days + tickSpan.days : ageSpan.days + units,
            0,
            0,
            0
          );
        }
      } else if (ageSpan.hours) {
        if (ageSpan.hours < min && !tickSpan.hours) {
          nextTickSpan = timespanForComponents(0, 0, 0, 0, 0, 60 * ageSpan.hours + units, 0);
        } else if (ageSpan.hours >= hoursInDay * min - 1) {
          nextTickSpan = timespanForComponents(0, 0, 0, min, 0, 0, 0, 0);
          unitChange = true;
        } else {
          nextTickSpan = timespanForComponents(
            ageSpan.years,
            ageSpan.months,
            ageSpan.weeks,
            ageSpan.days,
            tickSpan.hours ? ageSpan.hours + tickSpan.hours : ageSpan.hours + units,
            0,
            0
          );
        }
      } else if (ageSpan.minutes) {
        if (ageSpan.minutes < min && !tickSpan.minutes) {
          nextTickSpan = timespanForComponents(0, 0, 0, 0, 0, 0, 60 * ageSpan.minutes + units);
        } else if (ageSpan.minutes >= 60 * min - 1) {
          nextTickSpan = timespanForComponents(0, 0, 0, 0, min, 0, 0);
          unitChange = true;
        } else {
          nextTickSpan = timespanForComponents(
            ageSpan.years,
            ageSpan.months,
            ageSpan.weeks,
            ageSpan.days,
            ageSpan.hours,
            tickSpan.minutes ? ageSpan.minutes + tickSpan.minutes : ageSpan.minutes + units,
            0
          );
        }
      } else if (ageSpan.seconds) {
        if (ageSpan.seconds >= 60 * min - 1) {
          nextTickSpan = timespanForComponents(0, 0, 0, 0, 0, min, 0);
          unitChange = true;
        } else {
          nextTickSpan = timespanForComponents(
            ageSpan.years,
            ageSpan.months,
            ageSpan.weeks,
            ageSpan.days,
            ageSpan.hours,
            ageSpan.minutes,
            tickSpan.seconds ? ageSpan.seconds + tickSpan.seconds : ageSpan.seconds + units
          );
        }
      }

      previousDate = markDate;
      ageSpan = nextTickSpan;

      // Break if we reached the end date
      if (itemEndDate && previousDate.timestamp >= itemEndDate.timestamp) {
        break;
      }

      // Otherwise, calculate the next date
      let nextDate = dateFunctions.dateByAddingSpan(calendar, itemStartDate, nextTickSpan);
      if (itemEndDate && nextDate.timestamp > itemEndDate.timestamp) {
        nextDate = itemEndDate;
      }

      markDate = nextDate;
    }
  }

  return ticks;
};

const calcHeaderMinorTicks = (calendar, startDate, unitScale, rightTs, tickSpan) => {
  let ticks = [];

  // Major Ticks
  let markDate = startDate,
    nextDate = dateFunctions.dateByAddingSpan(calendar, markDate, tickSpan);

  // TODO: This test is only required for absolute date format...
  if (nextDate.era !== markDate.era) {
    nextDate = dateFunctions.dateAtEvenSpanBeforeDate(calendar, nextDate, tickSpan);
  }

  while (markDate.timestamp <= rightTs) {
    ticks.push({
      pixel: floatToInt(markDate.timestamp / unitScale),
      markDate: markDate
    });
    markDate = nextDate;
    nextDate = dateFunctions.dateByAddingSpan(calendar, nextDate, tickSpan);

    // TODO: This test is only required for absolute date format...
    if (nextDate.era !== markDate.era) {
      const testDate = dateFunctions.dateAtEvenSpanBeforeDate(calendar, nextDate, tickSpan);
      if (testDate.timestamp !== markDate.timestamp) {
        nextDate = testDate;
      }
    }
  }

  return ticks;
};

const getTimelineHeaderLabels = createCachedSelector(
  // fetch parameters for last calculate function
  getCalendar,
  getUnitScale,
  getTickInterval,
  getLabelScrollRange,
  // Calculate result
  (calendar, unitScale, tickInterval, scrollRange) => {
    const { left, right } = scrollRange;
    const rightTs = getLastTickInner(calendar, unitScale, right, tickInterval).timestamp;
    const startDate = getFirstTickInner(calendar, unitScale, left, tickInterval);
    const result = immutableRecords.TimelineHeaderDisplayTicksRecord({
      major: calcHeaderLabels(
        calendar,
        startDate,
        unitScale,
        rightTs,
        tickInterval.majorSpan,
        tickInterval.majorFormat
      ),
      minor: calcHeaderLabels(calendar, startDate, unitScale, rightTs, tickInterval.minorSpan, tickInterval.minorFormat)
    });
    return result;
  }
)(workViewSelectors.getViewId);

const getTimelineContextLabels = createCachedSelector(
  // fetch parameters for last calculate function
  getCalendar,
  getUnitScale,
  getRightPixel,
  getContextTickInterval,
  getFirstTick,
  // Calculate result
  (calendar, unitScale, rightPixel, tickInterval, startDate) => {
    const rightTs = floatToInt(rightPixel * unitScale);
    const result = immutableRecords.TimelineHeaderDisplayTicksRecord({
      major: calcHeaderLabels(calendar, startDate, unitScale, rightTs, tickInterval.majorSpan, tickInterval.majorFormat)
    });
    return result;
  }
)(workViewSelectors.getViewId);

const getTimelineAgeLabels = createCachedSelector(
  // fetch parameters for last calculate function
  getCalendar,
  getUnitScale,
  getRightPixel,
  getAgeTickInterval,
  getFirstTick,
  (_calendar, _viewId, _unitScale, _leftPixel, _rightPixel, _fontSize, itemStartDate, _itemEndDate) => itemStartDate,
  (_calendar, _viewId, _unitScale, _leftPixel, _rightPixel, _fontSize, _itemStartDate, itemEndDate) => itemEndDate,
  (_calendar, _viewId, _unitScale, _leftPixel, _rightPixel, _fontSize, _itemStartDate, itemEndDate, widthMap) =>
    widthMap,
  // Calculate result
  (calendar, unitScale, rightPixel, tickInterval, startDate, itemStartDate, itemEndDate, widthMap) => {
    const rightTs = floatToInt(rightPixel * unitScale);
    const result = calcAgeLabels(
      calendar,
      startDate,
      unitScale,
      rightTs,
      tickInterval.majorSpan,
      itemStartDate,
      itemEndDate,
      widthMap
    );
    return result;
  }
)(workViewSelectors.getViewId);

const getTimelineMinorTickDates = createCachedSelector(
  // fetch parameters for last calculate function
  getCalendar,
  getUnitScale,
  getRightPixel,
  getTickInterval,
  getFirstTick,
  // Calculate result
  (calendar, unitScale, rightPixel, tickInterval, startDate) => {
    const rightTs = floatToInt(rightPixel * unitScale);
    const result = calcHeaderMinorTicks(calendar, startDate, unitScale, rightTs, tickInterval.minorSpan);
    return result;
  }
)(workViewSelectors.getViewId);

const getContextViewEvents = createCachedSelector(
  // fetch parameters for last calculate function
  // (state, _viewIds) => state,
  (state, viewIds) => workViewSelectors.getItemsInGroupForViews(state, viewIds),
  // (_state, viewIds) => viewIds,
  // calendarSelectors.getCalendar,
  // Calculate result
  allItems => {
    // Get a list of all unique events in all the views
    // Now extract the values we want...
    return allItems.map(e => {
      // const startDate = e.startDate;
      // const endDate = dateFunctions.dateByAddingSpan(calendar, startDate, e.duration);

      return immutableRecords.ContextEventDotRecord({
        id: e.id,
        color: e.color,
        startTs: e.startDate.timestamp,
        endTs: e.endDate.timestamp
      });
    });
  }
)((_state, viewIds) => viewIds.first());

const filterObstructedContextDots = createCachedSelector(
  // inputs
  (dots, _unitScale, _fontSize, _viewId) => dots,
  (dots, unitScale, _fontSize, _viewId) => unitScale,
  (dots, unitScale, fontSize, _viewId) => fontSize,
  // calculation
  (dots, unitScale, fontSize) => {
    // Work out the size (in seconds) of a single dot
    const minWidthInTs = unitScale * scalePixelsToFontSize(timelineViewConstants.CONTEXT_DOT_SIZE, fontSize);

    // Keep track of the right-most dot we have drawn so far
    let coverageEndTs = null;

    // Sort them into order of start date, so that we know we can work through
    // them linearly
    const sortedDots = dots.sort((a, b) => a.startTs - b.startTs);

    // Filter any dots whose right edge is to the left of what we have seen so
    // far.
    const result = sortedDots.filter(e => {
      // if this goes beyond the last thing we have drawn so far, then
      // draw this too.
      if (coverageEndTs === null || e.endTs > coverageEndTs) {
        // update our coverageEnd for next time...
        // we apply the extra pixels of a circle to the end, just to
        // make sure we are being strict in what we cut out
        coverageEndTs = Math.max(e.endTs, e.startTs + minWidthInTs);
        return true;
      }
      return false;
    });
    return result;
  }
)((dots, unitScale, fontSize, viewId) => viewId);

const getTimelineViewDisplayCalendarMarkers = createSelector(
  // fetch parameters for last calculate function
  (markers, _calendar, _startTs, _endTs, _minRepeatSeconds) => markers,
  (_markers, calendar, _startTs, _endTs, _minRepeatSeconds) => calendar,
  (_markers, _calendar, startTs, _endTs, _minRepeatSeconds) => startTs,
  (_markers, _calendar, _startTs, endTs, _minRepeatSeconds) => endTs,
  (_markers, _calendar, _startTs, _endTs, minRepeatSeconds) => minRepeatSeconds,
  // Calculate result
  calendarMarkerSelectors.getDisplayCalendarMarkersBetweenDates
);

const getTimelineViewDisplayAnniversaries = createSelector(
  // fetch parameters for last calculate function
  (anniversaryItems, _typesById, _colorMap, _calendar, _startTs, _endTs, _minRepeatSeconds) => anniversaryItems,
  (_anniversaryItems, typesById, _colorMap, _calendar, _startTs, _endTs, _minRepeatSeconds) => typesById,
  (_anniversaryItems, _typesById, colorMap, _calendar, _startTs, _endTs, _minRepeatSeconds) => colorMap,
  (_anniversaryItems, _typesById, _colorMap, calendar, _startTs, _endTs, _minRepeatSeconds) => calendar,
  (_anniversaryItems, _typesById, _colorMap, _calendar, startTs, _endTs, _minRepeatSeconds) => startTs,
  (_anniversaryItems, _typesById, _colorMap, _calendar, _startTs, endTs, _minRepeatSeconds) => endTs,
  (_anniversaryItems, _typesById, _colorMap, _calendar, _startTs, _endTs, minRepeatSeconds) => minRepeatSeconds,
  (_anniversaryItems, _typesById, _colorMap, _calendar, _startTs, _endTs, _minRepeatSeconds, dateForNow) => dateForNow,
  // Calculate result
  itemSelectors.getDisplayAnniversariesBetweenDates
);

const getDropDateResultWithDelta = (state, origDate, deltaTs, unitScale, snapToTimespan) => {
  const calendar = calendarSelectors.getCalendar(state);
  const secondsSpan = timespanForPrecisionAndUnits(Precision.Second, deltaTs);
  const exactDate = dateFunctions.dateByAddingSpan(calendar, origDate, secondsSpan);
  const offset = dateSubtractionFunctions.timespanBetweenDates(calendar, origDate, exactDate);
  const roundedOffset = roundedTimespanForScale(calendar, offset, unitScale, snapToTimespan);
  const roundedDate = dateFunctions.dateByAddingSpan(calendar, origDate, roundedOffset);
  const precisionDiff = snapToTimespan
    ? (timespanToApproximateSeconds(calendar, roundedOffset) - deltaTs) / unitScale
    : 0;
  return {
    precisionDiff: precisionDiff,
    deltaSpan: roundedOffset,
    deltaString: stringFromTimespan(roundedOffset),
    newDate: roundedDate,
    dateString: stringFromDate(calendar, roundedDate)
  };
};

const getDropDateResult = (state, dropTs, unitScale, snapToTimespan) => {
  const calendar = calendarSelectors.getCalendar(state);
  return timelineUtils.getDropDateResult(calendar, dropTs, unitScale, snapToTimespan);
};

// TimelineCanvasView selectors

/**
 * Calculates the viewport based on the current scroll positions.
 * Note that the viewport is the entire contents we render (including overflow),
 * it is likely more than the user will see
 * Uses createSelector to cache the result so we ge the same viewport object
 * for multiple calls to help with other caching
 * @param {FlatRectRecord} containerRect
 * @param {number} scrollLeft
 * @param {number} scrolLRight
 */
const getViewportFromScrollPositions = createSelector(
  // inputs
  (xScrollPosition, _yScrollPosition) => xScrollPosition,
  (xScrollPosition, yScrollPosition) => yScrollPosition,
  // result
  (xScrollPosition, yScrollPosition) => {
    return immutableRecords.FlatRectRecord({
      left: xScrollPosition.viewport.min,
      right: xScrollPosition.viewport.max,
      width: xScrollPosition.viewport.range,
      top: yScrollPosition.viewport.min,
      bottom: yScrollPosition.viewport.max,
      height: yScrollPosition.viewport.range
    });
  }
);

const filterPositionsWithinRect = (timelineData, rect) => {
  return timelineData.update("groups", groups =>
    groups
      .filter(g => rectUtils.rectOverlapsRectY(g.bounds, rect))
      .map(g =>
        g.update("positions", positions =>
          rectUtils.rectOverlapsRectX(g.bounds, rect)
            ? positions.filter(pos => rectUtils.rectOverlapsRect(pos.totalRect, rect))
            : Map()
        )
      )
  );
};

/**
 * Gets all positions that fit within the current viewport
 * @param {TimelineDisplayRecord} timelineData
 * @param {FlatRectRecord} viewport
 */
const filterPositionsWithinViewport = createSelector(
  // input
  (timelineData, _viewport) => timelineData,
  (timelineData, viewport) => viewport,
  // output
  filterPositionsWithinRect
);

const filterEventsWithoutPositions = timelineData => {
  // first filter the positions
  const allEventIds = Set().withMutations(allIds => {
    timelineData.groups.forEach(g => allIds.union(g.positions.keys()));
  });
  return timelineData
    .update("eventsById", eventsById => eventsById.filter(e => allEventIds.has(e.id)))
    .update("sortedEventIds", sortedEventIds => sortedEventIds.filter(id => allEventIds.has(id)));
};

/**
 * Gets all events that exist within the current viewport
 * @param {TimelineDisplayRecord} timelineData
 * @param {FlatRectRecord} viewport
 */
const filterEventsWithinViewport = createSelector(
  // input
  (timelineData, viewport) => filterPositionsWithinViewport(timelineData, viewport),
  // output
  filterEventsWithoutPositions
);

// /**
//  * Gets all events that exist in the positions array (no spacial filtering applied)
//  * @param {List} events
//  * @param {Map} positions
//  */
// const getEventsWithPositions = createSelector(
//   // input
//   (events, _positions) => events,
//   (events, positions) => positions,
//   // output
//   (events, positions) => {
//     // first filter the positions
//     return events.filter(e => positions.has(e.id));
//   }
// );

/**
 * Calculates the exact region of the viewport that is visible to the user
 * based on the current scroll position.
 * Uses createSelector to cache the result so we ge the same viewport object
 * for multiple calls to help with other caching
 * @param {FlatRectRecord} containerRect
 * @param {number} scrollLeft
 * @param {number} scrolLRight
 */
const getVisiblePortal = createSelector(
  // inputs
  (containerRect, _scrollLeft, _scrollTop) => containerRect,
  (containerRect, scrollLeft, _scrollTop) => scrollLeft,
  (containerRect, scrollLeft, scrollTop) => scrollTop,
  // result
  (containerRect, scrollLeft, scrollTop) =>
    immutableRecords.FlatRectRecord({
      left: scrollLeft,
      top: scrollTop,
      width: containerRect.width,
      height: containerRect.height,
      right: scrollLeft + containerRect.width,
      bottom: scrollTop + containerRect.height
    })
);

/**
 * This function takes all positions, and returns those within the visiblePortal
 * inside the viewport.
 * It uses the cached result of positions within viewport to cut down the amount of
 * searching it needs to do within the list for efficiency
 * @param {TimelineDisplayRecord} timelineData
 * @param {FlatRectRecord} viewport
 * @param {FlatRectRecord} visiblePortal
 */
const filterPositionsWithinVisiblePortal = createSelector(
  // input
  (timelineData, viewport, _portal) => filterPositionsWithinViewport(timelineData, viewport),
  (timelineData, viewport, portal) => portal,
  // output
  filterPositionsWithinRect
);

/**
 * This function takes all events and positions, and returns those within the visiblePortal
 * inside the viewport.
 * It uses the cached result of events/positions within viewport to cut down the amount of
 * searching it needs to do within the list for efficiency
 * @param {TimelineDisplayRecord} timelineData
 * @param {FlatRectRecord} viewport
 * @param {FlatRectRecord} visiblePortal
 */
const filterEventsWithinVisiblePortal = createSelector(
  // input
  (timelineData, viewport, portal) => filterPositionsWithinVisiblePortal(timelineData, viewport, portal),
  // output
  filterEventsWithoutPositions
);

/**
 * This function finds the closest event for each cardinal direction.
 * We know that this function will only be called when there is nothing in the current viewport
 * so we can cheat and don't need to worry about whether they are within view or not
 * @param {TimelineDisplayRecord} timelineData
 * @param {FlatRectRecord} visiblePortal
 */
const getClosestEventPerDirection = createSelector(
  // Input
  (timelineData, _portal) => timelineData,
  (timelineData, portal) => portal,
  // Output
  (timelineData, portal) => {
    let distances = {
      left: null,
      right: null,
      top: null,
      bottom: null
    };

    let closestIds = {
      left: null,
      right: null,
      top: null,
      bottom: null
    };

    let horizontalAlignmentFound = false;

    timelineData.groups.forEach(group =>
      group.positions.forEach((p, eId) => {
        const rect = p.totalRect;

        // For left and right events: we only care about horizontal position
        // For above and below events: they must align to our portal horizontally to be considered
        // So we can divide events into three groups horizontally - left, right, and aligned
        if (rect.left < portal.left) {
          // Make sure it is to the left of our portal
          // And then record the closest (right-most) item
          if (rect.left > distances.left || isNil(distances.left)) {
            distances.left = rect.left;
            closestIds.left = eId;
          }
        } else if (rect.left > portal.right) {
          // Make sure it is to the right of our portal
          // And then record the closest (left-most) item
          if (rect.left < distances.right || isNil(distances.right)) {
            distances.right = rect.left;
            closestIds.right = eId;
          }
        } else {
          horizontalAlignmentFound = true;
          // If we reach here, the events are aligned with our viewport
          // so we can consider their vertical position
          if (rect.top < portal.top) {
            if (rect.top > distances.top || isNil(distances.top)) {
              distances.top = rect.top;
              closestIds.top = eId;
            }
          } else if (rect.top > portal.bottom) {
            if (rect.top < distances.bottom || isNil(distances.bottom)) {
              distances.bottom = rect.top;
              closestIds.bottom = eId;
            }
          }
        }
      })
    );

    // If we find something in horizontal alignment then we don't want to show
    // scroll left and scroll right indicators...
    if (horizontalAlignmentFound) {
      closestIds.left = null;
      closestIds.right = null;
    }

    return closestIds;
  }
);

const getAllowedGroups = createSelector(
  // fetch parameters for last calculate function
  state => definitionSelectors.getEnabledDefinitions(state).types,
  state => state.data.items,
  // Calculate the final result
  (types, items) => {
    const columnTypes = types.allIds.map(tId => types.byId.get(tId)).filter(t => t.showAsColumn);
    return columnTypes
      .map(t => {
        const groupOptions = items.allIdsForType
          .get(t.id)
          .map(oId => {
            const o = items.byId.get(oId);
            return immutableRecords.DropdownOptionRecord({
              value: o.id,
              label: getLabelWithUntitled(o.label)
            });
          })
          .push(
            immutableRecords.DropdownOptionRecord({
              value: timelineGroupOptions.getUnallocatedGroupIdForType(t.id),
              label: `No ${t.label} (Unallocated)`
            })
          );
        return immutableRecords.DropdownGroupRecord({
          value: t.id,
          label: t.label,
          children: groupOptions,
          allowAdd: false,
          disabled: !groupOptions.size
        });
      })
      .insert(
        0,
        immutableRecords.DropdownGroupRecord({
          value: TIMELINE_GROUP_SHOW_ALL,
          label: "All Events (No Grouping)",
          borderBelow: true,
          allowAdd: false
        })
      );
  }
);

const getAllowedGroupsForView = createCachedSelector(
  getAllowedGroups,
  appStateSelectors.getViewTimelineGroupIds,
  (allowedGroups, selectedGroups) => {
    return allowedGroups.map(cg => {
      return cg.withMutations(g => {
        let foundChecked = false;
        let foundUnchecked = false;
        let state = null;
        if (g.value !== TIMELINE_GROUP_SHOW_ALL) {
          g.update("children", children =>
            children.map(c => {
              const selected = selectedGroups.contains(c.value);
              if (selected) {
                foundChecked = true;
              } else {
                foundUnchecked = true;
              }
              return c.set("selected", selected);
            })
          );
          state =
            foundChecked && foundUnchecked
              ? CheckboxStates.MIXED
              : foundChecked
                ? CheckboxStates.CHECKED
                : CheckboxStates.UNCHECKED;
          g.set("state", state);
        } else {
          g.merge({
            state: selectedGroups.size === 0 ? CheckboxStates.CHECKED : CheckboxStates.UNCHECKED,
            disabled: selectedGroups.size === 0
          });
        }
      });
    });
  }
)(workViewSelectors.getViewId);

const getDependencyLinePosition = (isSubject, isStart, eventPosition, ongoing, layoutConstants) => {
  const overlappingOffset = 0.5;
  const markerWidth =
    eventPosition.markerRelativeRect.width +
    (eventPosition.startRectWithChildren.width
      ? eventPosition.startRectWithChildren.width + layoutConstants.MARKER_RIGHT_MARGIN
      : 0) +
    (eventPosition.endRectWithChildren.width
      ? eventPosition.endRectWithChildren.width + layoutConstants.MARKER_RIGHT_MARGIN
      : 0) +
    eventPosition.uncertainStartRect.width +
    eventPosition.uncertainEndRect.width +
    (ongoing ? layoutConstants.MARKER_HEIGHT / 2 : 0);
  const markerLeft =
    eventPosition.startRectWithChildren.left > 0
      ? eventPosition.startRectWithChildren.left
      : eventPosition.uncertainStartRect.left > 0
        ? eventPosition.uncertainStartRect.left
        : eventPosition.markerRelativeRect.left;
  const positionValue = {
    x: eventPosition.totalRect.left + markerLeft + (isStart ? overlappingOffset * 2 : markerWidth - overlappingOffset),
    y: eventPosition.totalRect.top + eventPosition.markerRelativeRect.top + eventPosition.markerRelativeRect.height / 2
  };
  const pointName = isSubject ? "subject" : "object";

  let depPos = {};
  depPos[`${pointName}Point`] = positionValue;
  depPos[`${pointName}Start`] = isStart;
  return depPos;
};

/**
 * @param {string} viewId               [0]
 * @param {TimelineDisplayRecord} data  [1]
 * @param {Map} validatedDependencies   [2]
 * @param {Map} validatedConstraints    [3]
 * @param {Object} layoutConstants      [4]
 * @param {Object} virtualOffset        [5]
 */
const calculateDependencyLinePositions = createCachedSelector(
  // inputs
  (...params) => params[1], // data
  (...params) => params[2], // validatedDependencies
  (...params) => params[3], // validatedConstraints
  (...params) => params[4], // layoutConstants
  // calculation
  (data, validatedDependencies, validatedConstraints, layoutConstants) => {
    let depLines = [];

    data.groups.forEach(g => {
      validatedDependencies.forEach(dep => {
        const subjectId = dep.subject;
        const objectId = dep.object;

        // Try to get the positions for the two items
        // within this group
        const subjectPos = g.positions.get(subjectId);
        const objectPos = g.positions.get(objectId);

        // we only want to render dependencies that fall entirely within the group
        if (!subjectPos || !objectPos) {
          return;
        }

        const subjectItem = data.eventsById.get(subjectId);
        const objectItem = data.eventsById.get(objectId);

        const subjectPoint = getDependencyLinePosition(
          true,
          timelineUtils.getIsDependencyStart(dep, true),
          subjectPos,
          subjectItem.ongoing,
          layoutConstants
        );

        const objectPoint = getDependencyLinePosition(
          false,
          timelineUtils.getIsDependencyStart(dep, false),
          objectPos,
          objectItem.ongoing,
          layoutConstants
        );

        // Ignore very long dependency lines
        const dx = Math.abs(subjectPoint.x - objectPoint.x);
        const dy = Math.abs(subjectPoint.y - objectPoint.y);

        if (dx > 10000 || dy > 10000) {
          return;
        }

        const line = Object.assign(
          {
            id: `${g.id}-${dep.id}`,
            isViolated: dep.isViolated
          },
          subjectPoint,
          objectPoint
        );

        depLines.push(line);
      });

      validatedConstraints.forEach(constraint => {
        const subjectId = constraint.subject;
        const objectId = constraint.object;

        // Try to get the positions for the two items
        // within this group
        const subjectPos = g.positions.get(subjectId);
        const objectPos = g.positions.get(objectId);

        // we only want to render dependencies that fall entirely within the group
        if (!subjectPos || !objectPos) {
          return;
        }

        const subjectItem = data.eventsById.get(subjectId);
        const objectItem = data.eventsById.get(objectId);

        const subjectPoint = getDependencyLinePosition(
          true,
          timelineUtils.getIsConstraintStart(constraint, true),
          subjectPos,
          subjectItem.ongoing,
          layoutConstants
        );

        const objectPoint = getDependencyLinePosition(
          false,
          timelineUtils.getIsConstraintStart(constraint, false),
          objectPos,
          objectItem.ongoing,
          layoutConstants
        );

        // Ignore very long dependency lines
        const dx = Math.abs(subjectPoint.x - objectPoint.x);
        const dy = Math.abs(subjectPoint.y - objectPoint.y);

        if (dx > 10000 || dy > 10000) {
          return;
        }

        const line = Object.assign(
          {
            id: `${g.id}-${constraint.id}`,
            isViolated: constraint.isViolated
          },
          subjectPoint,
          objectPoint
        );

        depLines.push(line);
      });
    });
    return depLines;
  }
)(viewId => viewId);

/**
 * @param {string} viewId               [0]
 * @param {TimelineDisplayRecord} data  [1]
 * @param {Map} validatedDependencies   [2]
 * @param {Map} validatedConstraints    [3]
 * @param {Object} layoutConstants      [4]
 * @param {Object} virtualOffset        [5]
 */
const calculateOffsetDependencyLinePositions = createCachedSelector(
  // inputs
  calculateDependencyLinePositions,
  (...params) => params[5], // layoutConstants
  // calculation
  (lines, virtualOffset) => {
    const offsetLines = map(lines, l => {
      const subjectPoint = {
        x: l.subjectPoint.x - virtualOffset.x,
        y: l.subjectPoint.y - virtualOffset.y
      };
      const objectPoint = {
        x: l.objectPoint.x - virtualOffset.x,
        y: l.objectPoint.y - virtualOffset.y
      };

      return Object.assign({}, l, {
        subjectPoint,
        objectPoint
      });
    });
    return offsetLines;
  }
)(viewId => viewId);

export default {
  getTimelineViewData,
  getTimelineViewSelectedData,
  getTimelineViewSortedItemIds,
  layoutEvents,
  getTimelineHeaderLabels,
  getTimelineContextLabels,
  getTimelineAgeLabels,
  getTimelineMinorTickDates,
  getTickInterval,
  getContextViewEvents,
  filterObstructedContextDots,
  getTimelineViewDisplayCalendarMarkers,
  getTimelineViewDisplayAnniversaries,
  getTimelineViewDataForItem,
  getDropDateResultWithDelta,
  getDropDateResult,
  getEventsOutOfRangeNumber,
  getActualDateFrom,
  getActualDateTo,

  // Used by TimelineCanvasView
  getViewportFromScrollPositions,
  filterEventsWithinViewport,
  // getEventsWithPositions,
  getVisiblePortal,
  filterEventsWithinVisiblePortal,
  getClosestEventPerDirection,

  getAllowedGroupsForView,

  calculateOffsetDependencyLinePositions
};
